import React, { useState } from 'react';
import { View, Text, StyleSheet, Alert } from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { ChatMessage } from '../../contexts/AIContext';
import { DataPreviewCard } from './DataPreviewCard';
import { AccessControlCard } from './AccessControlCard';
import { CompletionCard } from './CompletionCard';
import aiService from '../../services/aiService';

import { createLogger } from '../../utils/logger';
const logger = createLogger('AIMessageBubble');

/**
 * A questionnaire task (the preview's taskKey) → the save function it stands
 * for, which names the record's screen for "View data", and the dataType
 * the lock/unlock card understands.
 */
const QUESTIONNAIRE_TASKS: Record<string, { saveFunction: string; dataType: string }> = {
  medication: { saveFunction: 'save_medication', dataType: 'medication' },
  medications: { saveFunction: 'save_medication', dataType: 'medication' },
  current_medications: { saveFunction: 'save_medication', dataType: 'medication' },
  primary_physician: { saveFunction: 'save_primary_physician', dataType: 'primary physician' },
  specialty_physician: { saveFunction: 'save_specialty_physician', dataType: 'specialty physician' },
  caregiver: { saveFunction: 'save_caregiver', dataType: 'caregiver' },
  caregiver_info: { saveFunction: 'save_caregiver', dataType: 'caregiver' },
  medical_history: { saveFunction: 'save_medical_history', dataType: 'medical history' },
  current_therapy: { saveFunction: 'save_therapy', dataType: 'current therapy' },
  medical_equipment: { saveFunction: 'save_medical_equipment', dataType: 'medical equipment' },
  organ_donation: { saveFunction: 'save_organ_donation', dataType: 'organ donation' },
  identifying_info: { saveFunction: 'save_personal_info', dataType: 'personal information' },
  personal_info: { saveFunction: 'save_personal_info', dataType: 'personal information' },
  employment_info: { saveFunction: 'save_employment_info', dataType: 'employment' },
  spouse_info: { saveFunction: 'save_spouse_info', dataType: 'spouse' },
  children_info: { saveFunction: 'save_children_info', dataType: 'children' },
  pet_info: { saveFunction: 'save_pet_info', dataType: 'pet' },
};

interface AIMessageBubbleProps {
  message: ChatMessage;
  onNavigate?: (screen: string, params?: any) => void;
  onSendMessage: (message: string) => Promise<void>;
}

export const AIMessageBubble: React.FC<AIMessageBubbleProps> = ({ message, onNavigate, onSendMessage }) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const [previewVisible, setPreviewVisible] = useState(true);
  const [accessControlVisible, setAccessControlVisible] = useState(true);
  const [showCompletion, setShowCompletion] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedDataType, setSavedDataType] = useState<string | null>(null);
  // The record the user just confirmed: the app asks who can see it right
  // away instead of waiting for Orion to (item 4, owner decision 2026-09-23).
  const [confirmedRecord, setConfirmedRecord] = useState<{ dataType: string; dataId: string } | null>(null);

  if (isSystem) {
    return (
      <View style={styles.systemContainer}>
        <Text style={styles.systemText}>{message.content}</Text>
      </View>
    );
  }

  const getFunctionTitle = (functionName: string): string => {
    const titles: Record<string, string> = {
      save_beneficiary: 'Beneficiary Information',
      save_life_insurance: 'Life Insurance Policy',
      save_banking_account: 'Banking Account',
      save_attorney: 'Attorney Information',
      save_accountant: 'Accountant Information',
      save_investment_advisor: 'Investment Advisor Information',
      save_insurance_agent: 'Insurance Agent Information',
      save_tax_advisor: 'Tax Advisor Information',
      save_credit_card: 'Credit Card Information',
      save_banking_account: 'Banking Account',
      save_investment_account: 'Investment Account',
      save_loan: 'Loan Information',
      save_crypto: 'Cryptocurrency',
      save_primary_physician: 'Primary Physician',
      save_specialty_physician: 'Specialty Physician',
      save_medication: 'Medication',
      save_caregiver: 'Caregiver',
      save_medical_equipment: 'Medical Equipment',
      save_organ_donation: 'Organ Donation',
      save_therapy: 'Current Therapy',
      save_funeral_home: 'Funeral Home',
      save_burial_preferences: 'Burial Preferences',
      save_body_arrangements: 'Body Arrangements',
      save_casket_urn: 'Casket / Urn',
      save_food_preferences: 'Food Preferences',
      save_eulogy: 'Eulogy',
      save_pallbearers: 'Pallbearers',
      save_music: 'Music',
      save_funeral_instructions: 'Funeral Instructions',
      save_letter_to_loved_ones: 'Letter to Loved Ones',
      save_memorial_service: 'Memorial Service',
      save_prayers_readings: 'Prayers & Readings',
      save_officiant: 'Officiant',
      save_tribute_donations: 'Tribute Donations',
      save_traditions_rituals: 'Traditions & Rituals',
      save_wake_details: 'Wake Details',
      save_obituary: 'Obituary',
      save_headstone: 'Headstone',
      save_advance_directive: 'Advance Directive',
      save_ltc_preferences: 'Long-Term Care Preferences',
      save_dnr_preferences: 'DNR Preferences',
      save_breathing_tube_preferences: 'Breathing Tube Preferences',
      save_feeding_tube_preferences: 'Feeding Tube Preferences',
      save_medical_poa_preferences: 'Medical Power of Attorney',
      save_living_trust_preferences: 'Living Trust & Will',
      save_medical_history: 'Medical History',
      save_homeowners_insurance: 'Homeowners Insurance',
      save_cellphone_provider: 'Cellphone Provider',
      save_gas_provider: 'Gas Provider',
      save_home_ownership: 'Home Ownership / Living Arrangements',
      save_home_inventory: 'Home Inventory',
      save_electric_provider: 'Electric Provider',
      save_waste_provider: 'Waste Provider',
      save_water_sewer_provider: 'Water & Sewer Provider',
      save_home_security: 'Home Security',
      save_streaming_service_provider: 'Streaming Service',
      save_lawn_service_provider: 'Lawn Service',
      save_pest_control_provider: 'Pest Control',
      save_pool_service_provider: 'Pool Service',
      save_gym_membership: 'Gym Membership',
      save_personal_info: 'Personal Information',
      save_employment_info: 'Employment',
      save_spouse_info: 'Spouse',
      save_children_info: 'Children',
      save_pet_info: 'Pets',
    };
    return titles[functionName] || 'Information Preview';
  };

  const getNavigationScreenForFunction = (functionName: string): { stack: string; screen: string; checklist?: string } | null => {
    const navigationMap: Record<string, { stack: string; screen: string; checklist?: string }> = {
      save_primary_physician: { stack: 'Medical', screen: 'PrimaryPhysicianData', checklist: 'MedicalChecklist' },
      save_specialty_physician: { stack: 'Medical', screen: 'SpecialtyPhysicianData', checklist: 'MedicalChecklist' },
      save_medication: { stack: 'Medical', screen: 'MedicationsData', checklist: 'MedicalChecklist' },
      save_caregiver: { stack: 'Medical', screen: 'CaregiverData', checklist: 'MedicalChecklist' },
      save_medical_equipment: { stack: 'Medical', screen: 'MedicalEquipmentData', checklist: 'MedicalChecklist' },
      save_organ_donation: { stack: 'Medical', screen: 'OrganDonorData', checklist: 'MedicalChecklist' },
      save_therapy: { stack: 'Medical', screen: 'TreatmentsData', checklist: 'MedicalChecklist' },
      save_medical_history: { stack: 'Medical', screen: 'MedicalHistoryData', checklist: 'MedicalChecklist' },
      save_attorney: { stack: 'Finance', screen: 'AttorneyInfoData', checklist: 'FinanceChecklist' },
      save_accountant: { stack: 'Finance', screen: 'AccountantInfoData', checklist: 'FinanceChecklist' },
      save_investment_advisor: { stack: 'Finance', screen: 'InvestmentAdvisorInfoData', checklist: 'FinanceChecklist' },
      save_insurance_agent: { stack: 'Finance', screen: 'InsuranceAgentInfoData', checklist: 'FinanceChecklist' },
      save_tax_advisor: { stack: 'Finance', screen: 'TaxAdvisorInfoData', checklist: 'FinanceChecklist' },
      save_credit_card: { stack: 'Finance', screen: 'CreditCardInfoData', checklist: 'FinanceChecklist' },
      save_banking_account: { stack: 'Finance', screen: 'BankingInfoData', checklist: 'FinanceChecklist' },
      save_investment_account: { stack: 'Finance', screen: 'InvestmentsInfoData', checklist: 'FinanceChecklist' },
      save_loan: { stack: 'Finance', screen: 'LoanInfoData', checklist: 'FinanceChecklist' },
      save_crypto: { stack: 'Finance', screen: 'CryptoInfoData', checklist: 'FinanceChecklist' },
      save_beneficiary: { stack: 'Finance', screen: 'BeneficiaryDataScreen', checklist: 'FinanceChecklist' },
      save_life_insurance: { stack: 'Finance', screen: 'LifeInsuranceDataScreen', checklist: 'FinanceChecklist' },
      save_funeral_home: { stack: 'Wishes', screen: 'FuneralHomeData', checklist: 'FinalWishesChecklist' },
      save_burial_preferences: { stack: 'Wishes', screen: 'BurialPlotData', checklist: 'FinalWishesChecklist' },
      save_body_arrangements: { stack: 'Wishes', screen: 'BodyArrangementsData', checklist: 'FinalWishesChecklist' },
      save_casket_urn: { stack: 'Wishes', screen: 'CasketUrnData', checklist: 'FinalWishesChecklist' },
      save_food_preferences: { stack: 'Wishes', screen: 'FoodPreferencesData', checklist: 'FinalWishesChecklist' },
      save_eulogy: { stack: 'Wishes', screen: 'EulogyData', checklist: 'FinalWishesChecklist' },
      save_pallbearers: { stack: 'Wishes', screen: 'PallBearerData', checklist: 'FinalWishesChecklist' },
      save_music: { stack: 'Wishes', screen: 'MusicData', checklist: 'FinalWishesChecklist' },
      save_funeral_instructions: { stack: 'Wishes', screen: 'FuneralInstructionsData', checklist: 'FinalWishesChecklist' },
      save_letter_to_loved_ones: { stack: 'Wishes', screen: 'LetterToLovedOnesData', checklist: 'FinalWishesChecklist' },
      save_memorial_service: { stack: 'Wishes', screen: 'MemorialServiceData', checklist: 'FinalWishesChecklist' },
      save_prayers_readings: { stack: 'Wishes', screen: 'PrayersReadingsData', checklist: 'FinalWishesChecklist' },
      save_officiant: { stack: 'Wishes', screen: 'OfficiantData', checklist: 'FinalWishesChecklist' },
      save_tribute_donations: { stack: 'Wishes', screen: 'TributeDonationsData', checklist: 'FinalWishesChecklist' },
      save_traditions_rituals: { stack: 'Wishes', screen: 'TraditionsRitualsData', checklist: 'FinalWishesChecklist' },
      save_wake_details: { stack: 'Wishes', screen: 'WakeDetailsData', checklist: 'FinalWishesChecklist' },
      save_obituary: { stack: 'Wishes', screen: 'ObituaryDetailsData', checklist: 'FinalWishesChecklist' },
      save_headstone: { stack: 'Wishes', screen: 'HeadstoneDetailsData', checklist: 'FinalWishesChecklist' },
      save_homeowners_insurance: { stack: 'Property', screen: 'HomeownersInsuranceData', checklist: 'PropertyChecklist' },
      save_cellphone_provider: { stack: 'Property', screen: 'CellphoneProviderData', checklist: 'PropertyChecklist' },
      save_gas_provider: { stack: 'Property', screen: 'GasProviderData', checklist: 'PropertyChecklist' },
      save_home_ownership: { stack: 'Property', screen: 'HomeOwnershipData', checklist: 'PropertyChecklist' },
      save_home_inventory: { stack: 'Property', screen: 'HomeInventoryDataScreen', checklist: 'PropertyChecklist' },
      save_electric_provider: { stack: 'Property', screen: 'ElectricProviderData', checklist: 'PropertyChecklist' },
      save_waste_provider: { stack: 'Property', screen: 'WasteProviderData', checklist: 'PropertyChecklist' },
      save_water_sewer_provider: { stack: 'Property', screen: 'WaterSewerProviderData', checklist: 'PropertyChecklist' },
      save_home_security: { stack: 'Property', screen: 'HomeSecurityData', checklist: 'PropertyChecklist' },
      save_streaming_service_provider: { stack: 'Property', screen: 'StreamingServiceProviderData', checklist: 'PropertyChecklist' },
      save_lawn_service_provider: { stack: 'Property', screen: 'LawnServiceProviderData', checklist: 'PropertyChecklist' },
      save_pest_control_provider: { stack: 'Property', screen: 'PestControlProviderData', checklist: 'PropertyChecklist' },
      save_pool_service_provider: { stack: 'Property', screen: 'PoolServiceProviderData', checklist: 'PropertyChecklist' },
      save_gym_membership: { stack: 'Property', screen: 'GymMembershipData', checklist: 'PropertyChecklist' },
      save_advance_directive: { stack: 'FinalCare', screen: 'AdvanceDirectiveData', checklist: 'FinalCareChecklist' },
      save_ltc_preferences: { stack: 'FinalCare', screen: 'LTCPreferencesData', checklist: 'FinalCareChecklist' },
      save_dnr_preferences: { stack: 'FinalCare', screen: 'DNRPreferencesData', checklist: 'FinalCareChecklist' },
      save_breathing_tube_preferences: { stack: 'FinalCare', screen: 'BreathingTubePreferencesData', checklist: 'FinalCareChecklist' },
      save_feeding_tube_preferences: { stack: 'FinalCare', screen: 'FeedingTubePreferencesData', checklist: 'FinalCareChecklist' },
      save_medical_poa_preferences: { stack: 'FinalCare', screen: 'MedicalPOAPreferencesData', checklist: 'FinalCareChecklist' },
      save_living_trust_preferences: { stack: 'FinalCare', screen: 'LivingTrustPreferencesData', checklist: 'FinalCareChecklist' },
      save_pet_care_preferences: { stack: 'Personal', screen: 'PetCarePreferencesData', checklist: 'PersonalChecklist' },
      save_personal_info: { stack: 'Personal', screen: 'PersonalInfoData', checklist: 'PersonalChecklist' },
      save_employment_info: { stack: 'Personal', screen: 'EmploymentInfoData', checklist: 'PersonalChecklist' },
      save_spouse_info: { stack: 'Personal', screen: 'SpouseInfoData', checklist: 'PersonalChecklist' },
      save_children_info: { stack: 'Personal', screen: 'ChildrenInfoData', checklist: 'PersonalChecklist' },
      save_pet_info: { stack: 'Personal', screen: 'PetInfoData', checklist: 'PersonalChecklist' },
    };
    return navigationMap[functionName] || null;
  };

  const handleConfirm = async () => {
    if (!message.functionCall) return;

    setIsSaving(true);
    try {
      // NEW PATTERN: Frontend saves the data when user clicks Confirm
      const taskKey = message.functionCall.result?.data?.taskKey;

      if (!taskKey) {
        logger.error('No taskKey found in function result');
        Alert.alert('Error', 'Unable to save: missing task information');
        return;
      }

      logger.debug(`[AIMessageBubble] Saving ${taskKey} entry...`);

      // Call the API to save the data
      const response = await aiService.confirmSaveEntry(taskKey);

      if (!response.success) {
        throw new Error(response.error || 'Failed to save data');
      }

      logger.debug(`[AIMessageBubble] Entry saved successfully:`, response.data);

      // Hide the preview
      setPreviewVisible(false);

      // Store the saved ID for potential lock/unlock flow
      const savedId = response.data?.id;
      if (savedId) {
        logger.debug(`[AIMessageBubble] Saved ID: ${savedId}`);
      }

      // Ask who can see the record now, in this bubble. The card used to
      // wait for Orion to call ask_lock_unlock_preference with this id, and
      // when it did not, the user never chose and never got "View data"
      // (beta 2026-09-18). The server refuses a second card for this id.
      const task = QUESTIONNAIRE_TASKS[taskKey];
      setSavedDataType(task?.saveFunction ?? message.functionCall.name);
      if (savedId) {
        setConfirmedRecord({ dataType: task?.dataType ?? taskKey.replace(/_/g, ' '), dataId: savedId });
        setAccessControlVisible(true);
      }

      await onSendMessage(
        savedId
          ? "Yes, that entry looks good. I've confirmed it, and the app is asking me who can see it."
          : "Yes, that entry looks good. I've confirmed it.",
      );

    } catch (error) {
      logger.error('Error confirming data:', error);
      Alert.alert('Error', 'Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!message.functionCall) return;

    // Send a message to Orion asking to edit
    const dataType = getFunctionTitle(message.functionCall.name);
    await onSendMessage(`I'd like to edit the ${dataType} information. Can you help me modify it?`);
  };

  const handleCancel = async () => {
    setPreviewVisible(false);

    // Send cancellation message to Orion
    if (message.functionCall) {
      const dataType = getFunctionTitle(message.functionCall.name);
      await onSendMessage(`Actually, I don't want to save the ${dataType} right now.`);
    }
  };

  const handleAccessControlComplete = async (selectedEmails: string[], lockStatus: 'locked' | 'unlocked') => {
    setAccessControlVisible(false);
    setIsSaving(true);

    try {
      // Send confirmation message to Orion
      const statusMessage = lockStatus === 'locked'
        ? `I've locked the information. ${selectedEmails.length > 0 ? `I've shared it with ${selectedEmails.join(', ')}.` : ''}`
        : `I've unlocked the information so everyone with access can see it.`;

      await onSendMessage(statusMessage);

      // The confirmed record already named its screen (savedDataType).
      // Otherwise take the dataType from Orion's ask_lock_unlock_preference,
      // since each message bubble has its own state.
      if (!confirmedRecord && message.functionCall?.arguments?.dataType) {
        // Map the dataType to the save function name format
        const dataType = message.functionCall.arguments.dataType.toLowerCase();
        let functionName = '';

        if (dataType.includes('accountant')) {
          functionName = 'save_accountant';
        } else if (dataType.includes('attorney')) {
          functionName = 'save_attorney';
        } else if (dataType.includes('investment advisor')) {
          functionName = 'save_investment_advisor';
        } else if (dataType.includes('insurance agent')) {
          functionName = 'save_insurance_agent';
        } else if (dataType.includes('tax advisor')) {
          functionName = 'save_tax_advisor';
        } else if (dataType.includes('credit card')) {
          functionName = 'save_credit_card';
        } else if (dataType.includes('banking') || dataType.includes('bank account')) {
          functionName = 'save_banking_account';
        } else if (dataType.includes('investment account')) {
          functionName = 'save_investment_account';
        } else if (dataType.includes('loan')) {
          functionName = 'save_loan';
        } else if (dataType.includes('crypto')) {
          functionName = 'save_crypto';
        } else if (dataType.includes('primary physician')) {
          functionName = 'save_primary_physician';
        } else if (dataType.includes('specialty physician')) {
          functionName = 'save_specialty_physician';
        } else if (dataType.includes('medication')) {
          functionName = 'save_medication';
        } else if (dataType.includes('caregiver')) {
          functionName = 'save_caregiver';
        } else if (dataType.includes('medical equipment')) {
          functionName = 'save_medical_equipment';
        } else if (dataType.includes('organ')) {
          functionName = 'save_organ_donation';
        } else if (dataType.includes('therapy') || dataType.includes('treatment')) {
          functionName = 'save_therapy';
        } else if (dataType.includes('medical history')) {
          functionName = 'save_medical_history';
        } else if (dataType.includes('homeowners insurance') || dataType.includes('homeowner insurance')) {
          functionName = 'save_homeowners_insurance';
        } else if (dataType.includes('cellphone') || dataType.includes('cell phone')) {
          functionName = 'save_cellphone_provider';
        } else if (dataType.includes('gas provider') || dataType.includes('gas utility')) {
          functionName = 'save_gas_provider';
        } else if (dataType.includes('home ownership') || dataType.includes('living arrangement')) {
          functionName = 'save_home_ownership';
        } else if (dataType.includes('home inventory') || dataType.includes('home_inventory')) {
          functionName = 'save_home_inventory';
        } else if (dataType.includes('electric') || dataType.includes('electricity')) {
          functionName = 'save_electric_provider';
        } else if (dataType.includes('waste') || dataType.includes('trash') || dataType.includes('garbage')) {
          functionName = 'save_waste_provider';
        } else if (dataType.includes('water') || dataType.includes('sewer')) {
          functionName = 'save_water_sewer_provider';
        } else if (dataType.includes('home security') || dataType.includes('security system')) {
          functionName = 'save_home_security';
        } else if (dataType.includes('streaming') || dataType.includes('netflix') || dataType.includes('hulu')) {
          functionName = 'save_streaming_service_provider';
        } else if (dataType.includes('lawn') || dataType.includes('landscaping')) {
          functionName = 'save_lawn_service_provider';
        } else if (dataType.includes('pest control') || dataType.includes('exterminator')) {
          functionName = 'save_pest_control_provider';
        } else if (dataType.includes('pool')) {
          functionName = 'save_pool_service_provider';
        } else if (dataType.includes('gym') || dataType.includes('fitness')) {
          functionName = 'save_gym_membership';
        } else if (dataType.includes('funeral home')) {
          functionName = 'save_funeral_home';
        } else if (dataType.includes('burial') || dataType.includes('cremation')) {
          functionName = 'save_burial_preferences';
        } else if (dataType.includes('body arrangement')) {
          functionName = 'save_body_arrangements';
        } else if (dataType.includes('casket') || dataType.includes('urn')) {
          functionName = 'save_casket_urn';
        } else if (dataType.includes('food preference')) {
          functionName = 'save_food_preferences';
        } else if (dataType.includes('eulogy')) {
          functionName = 'save_eulogy';
        } else if (dataType.includes('pallbearer')) {
          functionName = 'save_pallbearers';
        } else if (dataType.includes('music')) {
          functionName = 'save_music';
        } else if (dataType.includes('funeral instruction')) {
          functionName = 'save_funeral_instructions';
        } else if (dataType.includes('letter')) {
          functionName = 'save_letter_to_loved_ones';
        } else if (dataType.includes('memorial service')) {
          functionName = 'save_memorial_service';
        } else if (dataType.includes('prayer') || dataType.includes('reading')) {
          functionName = 'save_prayers_readings';
        } else if (dataType.includes('officiant')) {
          functionName = 'save_officiant';
        } else if (dataType.includes('donation') || dataType.includes('tribute')) {
          functionName = 'save_tribute_donations';
        } else if (dataType.includes('tradition') || dataType.includes('ritual')) {
          functionName = 'save_traditions_rituals';
        } else if (dataType.includes('wake')) {
          functionName = 'save_wake_details';
        } else if (dataType.includes('obituary')) {
          functionName = 'save_obituary';
        } else if (dataType.includes('headstone') || dataType.includes('grave marker')) {
          functionName = 'save_headstone';
        } else if (dataType.includes('advance directive')) {
          functionName = 'save_advance_directive';
        } else if (dataType.includes('ltc') || dataType.includes('long-term care') || dataType.includes('long term care')) {
          functionName = 'save_ltc_preferences';
        } else if (dataType.includes('dnr') || dataType.includes('do not resuscitate') || dataType.includes('resuscitation')) {
          functionName = 'save_dnr_preferences';
        } else if (dataType.includes('breathing tube') || dataType.includes('ventilat') || dataType.includes('mechanical breathing')) {
          functionName = 'save_breathing_tube_preferences';
        } else if (dataType.includes('feeding tube') || dataType.includes('artificial nutrition') || dataType.includes('tube feeding')) {
          functionName = 'save_feeding_tube_preferences';
        } else if (dataType.includes('medical poa') || dataType.includes('medical power of attorney') || dataType.includes('healthcare proxy') || dataType.includes('healthcare agent')) {
          functionName = 'save_medical_poa_preferences';
        } else if (dataType.includes('living trust') || dataType.includes('will') || dataType.includes('trust and will') || dataType.includes('trust & will')) {
          functionName = 'save_living_trust_preferences';
        } else if (dataType.includes('pet care') || dataType.includes('pet') || dataType.includes('animal care')) {
          functionName = 'save_pet_care_preferences';
        }

        if (functionName) {
          setSavedDataType(functionName);
        }
      }

      // Show the completion card with navigation options for both lock and unlock
      // This provides the success message and action buttons the user expects
      setShowCompletion(true);
    } catch (error) {
      logger.error('Error setting access control:', error);
      Alert.alert('Error', 'Failed to set access permissions. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCompletionClose = () => {
    setShowCompletion(false);
  };

  const handleAccessControlCancel = async () => {
    setAccessControlVisible(false);
    await onSendMessage('Actually, let me decide about access control later.');
  };

  return (
    <View style={[styles.container, isUser ? styles.userContainer : styles.assistantContainer]}>
      <View style={[styles.bubble, isUser ? styles.userBubble : styles.assistantBubble]}>
        <Text style={[styles.text, isUser ? styles.userText : styles.assistantText]}>
          {String(message.content || '')}
        </Text>
        {message.timestamp && typeof message.timestamp === 'number' && (
          <Text style={[styles.timestamp, isUser ? styles.userTimestamp : styles.assistantTimestamp]}>
            {new Date(message.timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        )}
      </View>

      {/* Show data preview card for review_collected_data */}
      {!isUser && message.functionCall && previewVisible &&
       message.functionCall.name === 'review_collected_data' && (
        <DataPreviewCard
          title={getFunctionTitle(message.functionCall.name)}
          data={message.functionCall.result?.data?.collectedData || message.functionCall.arguments}
          formattedPreview={message.functionCall.result?.data?.formattedPreview}
          onConfirm={handleConfirm}
          onEdit={handleEdit}
          onCancel={handleCancel}
        />
      )}

      {/* Who can see the record the user just confirmed */}
      {!isUser && confirmedRecord && accessControlVisible && (
        <View style={styles.cardWrapper}>
          <AccessControlCard
            dataType={confirmedRecord.dataType}
            dataId={confirmedRecord.dataId}
            onComplete={handleAccessControlComplete}
            onCancel={handleAccessControlCancel}
          />
        </View>
      )}

      {/* Show access control card for lock/unlock decisions (not for a
          refused call: the server says no when this record was already asked) */}
      {!isUser && message.functionCall && accessControlVisible && !confirmedRecord &&
       message.functionCall.result?.success !== false &&
       (message.functionCall.name === 'get_successors_keyholders' ||
        message.functionCall.name === 'ask_lock_unlock_preference') && (
        <View style={styles.cardWrapper}>
          <AccessControlCard
            dataType={message.functionCall.arguments?.dataType || 'information'}
            dataId={message.functionCall.arguments?.dataId || ''}
            onComplete={handleAccessControlComplete}
            onCancel={handleAccessControlCancel}
          />
        </View>
      )}

      {/* Show completion card after access control is set */}
      {!isUser && showCompletion && savedDataType && (
        <View style={styles.cardWrapper}>
          <CompletionCard
            taskType={getFunctionTitle(savedDataType)}
            onViewData={
              onNavigate && getNavigationScreenForFunction(savedDataType)
                ? () => {
                    const nav = getNavigationScreenForFunction(savedDataType);
                    if (nav) {
                      onNavigate(nav.stack, { screen: nav.screen });
                    }
                  }
                : undefined
            }
            onViewChecklist={
              onNavigate && getNavigationScreenForFunction(savedDataType)
                ? () => {
                    const nav = getNavigationScreenForFunction(savedDataType);
                    if (nav?.checklist) {
                      onNavigate('Dashboard', { screen: nav.checklist, params: { refresh: Date.now() } });
                    }
                  }
                : undefined
            }
            onClose={handleCompletionClose}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: ms(4),
    marginHorizontal: ms(12),
  },
  userContainer: {
    alignItems: 'flex-end',
  },
  assistantContainer: {
    alignItems: 'flex-start',
  },
  cardWrapper: {
    width: '95%',
    alignSelf: 'stretch',
  },
  bubble: {
    maxWidth: '80%',
    paddingHorizontal: ms(16),
    paddingVertical: ms(10),
    borderRadius: ms(18),
  },
  userBubble: {
    backgroundColor: '#29BFC0',
    borderBottomRightRadius: ms(4),
  },
  assistantBubble: {
    backgroundColor: '#F0F0F0',
    borderBottomLeftRadius: ms(4),
  },
  text: {
    fontSize: ms(16, 0.3),
    lineHeight: ms(22, 0.3),
  },
  userText: {
    color: '#FFFFFF',
  },
  assistantText: {
    color: '#333333',
  },
  timestamp: {
    fontSize: ms(11, 0.3),
    marginTop: ms(4),
  },
  userTimestamp: {
    color: 'rgba(255, 255, 255, 0.7)',
    textAlign: 'right',
  },
  assistantTimestamp: {
    color: 'rgba(0, 0, 0, 0.5)',
    textAlign: 'left',
  },
  systemContainer: {
    marginVertical: ms(8),
    marginHorizontal: ms(12),
    alignItems: 'center',
  },
  systemText: {
    fontSize: ms(13, 0.3),
    color: '#888888',
    fontStyle: 'italic',
  },
});
